import { Folder } from '@/api/generatedApi';
import { useCreateFolder } from '@/hooks/api/folders/useCreateFolder';
import { useOptimisticFolderName } from '@/hooks/useOptimisticFolderName';
import { createUseStyles } from '@/services/createUseStyles';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const useStyles = createUseStyles(theme => ({
  CreateFolder_dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',

    '&:first-of-type': {
      paddingTop: theme.spacing(3),
    },
  },
}));

export const CreateFolderModal = ({
  isOpen,
  onClose,
  onCreate,
  parentFolder,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate?: (folderId: string) => void;
  parentFolder?: Folder;
}) => {
  const c = useStyles();

  const [searchParameters, setSearchParameters] = useSearchParams();
  const { createFolder, isCreating, createError } = useCreateFolder({ parentFolder });
  const { setOptimisticFolder } = useOptimisticFolderName();

  const [newFolderName, setNewFolderName] = useState('');

  const canSubmit = newFolderName.trim() && !isCreating;

  const handleSubmit = () => {
    const folderName = newFolderName.trim();
    createFolder(folderName, {
      onSuccess: ({ data }) => {
        setOptimisticFolder(data.folder.id, folderName);
        setNewFolderName('');
        onClose();
        onCreate?.(data.folder.id);
        const updatedParameters = new URLSearchParams(searchParameters);
        updatedParameters.set('folderId', data.folder.id);
        setSearchParameters(updatedParameters);
      },
    });
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewFolderName(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && canSubmit) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={onClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
            width: '405px',
          },
        },
      }}
    >
      <DialogTitle>Create New Folder</DialogTitle>
      <DialogContent className={c.CreateFolder_dialogContent}>
        {createError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {createError.message}
          </Alert>
        )}
        <TextField
          required
          label='Folder Name'
          value={newFolderName}
          onChange={handleNameChange}
          onKeyDown={handleKeyDown}
          fullWidth
          error={!!createError}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSubmit} disabled={!canSubmit} sx={{ width: '100px' }}>
          {isCreating ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
